import VoiceSettingsModal from "@/components/modal/VoiceSettingsModal";
import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import { useGrammarHandlers } from "@/lib/hooks/slides/editor/handlers/grammar/useGrammarHandlers";
import { useWordHandlers } from "@/lib/hooks/slides/editor/handlers/word/useWordHandlers";
import { useLocalAudioUpload } from "@/lib/hooks/slides/editor/useLocalAudioUpload";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import { type GrammarSlideData, MediaUsageContextSchema } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { DEFAULT_VOICE } from "@ai-teach/utils";
import { useEffect, useState } from "react";
import { AiFillPicture } from "react-icons/ai";
import { FaMagic, FaMusic, FaPause, FaPlay } from "react-icons/fa";
import { HiX } from "react-icons/hi";
import { useSlideEditorContext } from "../../../SlideEditorContext";
import { EditorContainer } from "../../EditorContainer";

export function GrammarSentenceEditor() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVoiceSettingsOpen, setIsVoiceSettingsOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const dispatch = useAppDispatch();
  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const grammarHandlers = useGrammarHandlers(slide ?? null);
  const { handleFileDrop: handleImageFileDrop, isUploading: isUploadingImage } =
    useLocalImageUpload((media) => grammarHandlers.addImage?.(media));
  const { handleFileDrop: handleAudioFileDrop } = useLocalAudioUpload((media) =>
    grammarHandlers.addAudio?.(media)
  );

  const grammarSlideData = slide?.slideData as GrammarSlideData | undefined;

  if (!grammarSlideData) {
    return null;
  }

  const { text, image, audio, correctAnswer } = grammarSlideData;
  const slideName = slide?.name || "";

  const { setActivePanel } = useSlideEditorContext();

  const [wordAudioEl, setWordAudioEl] = useState<HTMLAudioElement | null>(null);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleRemoveMedia = (type: "audio" | "image") => {
    if (!slide) return;
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...grammarSlideData,
          [type]: { id: null, src: null, filename: null },
        },
      })
    );
  };

  const handlePlayPause = () => {
    if (!wordAudioEl) return;

    if (isPlaying) {
      wordAudioEl.pause();
      setIsPlaying(false);
    } else {
      wordAudioEl.play();
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    if (!wordAudioEl) return;

    const handleTimeUpdate = () => {
      if (!wordAudioEl.ended) {
        setCurrentTime(wordAudioEl.currentTime);
      }
    };
    const handleLoadedMetadata = () => setDuration(wordAudioEl.duration);
    const handleEnded = () => {
      wordAudioEl.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    };

    wordAudioEl.addEventListener("timeupdate", handleTimeUpdate);
    wordAudioEl.addEventListener("loadedmetadata", handleLoadedMetadata);
    wordAudioEl.addEventListener("ended", handleEnded);

    return () => {
      wordAudioEl.removeEventListener("timeupdate", handleTimeUpdate);
      wordAudioEl.removeEventListener("loadedmetadata", handleLoadedMetadata);
      wordAudioEl.removeEventListener("ended", handleEnded);
    };
  }, [wordAudioEl]);

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <EditorContainer>
      {/* Header */}
      <div className="flex items-center justify-between shrink-0 px-3">
        <h2 className="text-sm font-semibold text-primary-900 truncate uppercase tracking-widest">
          {slideName || "Question Slide"}
        </h2>
        <span className="text-[10px] font-medium text-gray-100 bg-primary-900 px-2 py-0.5 rounded-full shrink-0">
          Grammar · Sentence
        </span>
      </div>

      {/* GROUP 1: Question + Audio — single card */}
      <div className="flex-1 min-h-0 rounded-xl border border-border overflow-hidden flex">
        {/* Question panel */}
        <div className="flex-1 bg-surface p-3 flex flex-col">
          <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1.5 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
            Question
          </label>
          <textarea
            value={text || ""}
            onChange={(e) => {
              if (!slide) return;
              dispatch(
                updateSlide({
                  slideId: slide.id,
                  slideData: { ...grammarSlideData, text: e.target.value },
                })
              );
            }}
            className="flex-1 w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm font-medium focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition-all placeholder:text-muted-foreground/50 resize-none"
            placeholder="Enter question..."
          />
        </div>

        {/* Divider */}
        <div className="w-px bg-border shrink-0" />

        {/* Audio panel */}
        <div className="flex-1 min-w-0 bg-surface flex flex-col">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50/70 dark:bg-blue-950/20 border-b border-blue-100 dark:border-blue-900/30 shrink-0">
            <span className="text-[10px] font-semibold text-blue-500 dark:text-blue-400 uppercase tracking-widest">
              Question audio
            </span>
            <div className="flex-1 h-px bg-blue-200/60 dark:bg-blue-800/30" />
            <button
              type="button"
              onClick={() => setIsVoiceSettingsOpen(true)}
              className="flex items-center gap-1.5 text-[10px] font-semibold text-blue-100 bg-blue-900 hover:bg-blue-700 px-2.5 py-1 rounded-full transition-colors whitespace-nowrap"
            >
              <FaMagic className="text-[9px]" />
              Generate
            </button>
          </div>

          {audio?.src && (
            <audio ref={setWordAudioEl} src={audio.src} preload="metadata">
              <track kind="captions" srcLang="en" default />
            </audio>
          )}

          {slide && (
            <DroppableZone
              id={`${slide.id}-audio`}
              data={{ id: slide.id, type: "word-audio" }}
              allowedType={["audio"]}
              className="relative flex-1"
              onLocalFileDrop={handleAudioFileDrop}
              onClick={() => setActivePanel("Media")}
            >
              {audio?.src ? (
                <div className="flex flex-col justify-center gap-2 px-3 py-3 h-full">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlayPause();
                      }}
                      className="shrink-0 w-8 h-8 rounded-full bg-blue-500 hover:bg-blue-600 flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-sm"
                    >
                      {isPlaying ? (
                        <FaPause className="text-white text-[10px]" />
                      ) : (
                        <FaPlay className="text-white text-[10px] ml-0.5" />
                      )}
                    </button>
                    <p className="flex-1 text-xs font-medium text-foreground truncate min-w-0">
                      {audio.filename || "Question audio"}
                    </p>
                    <IconButton
                      type="button"
                      variant="danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveMedia("audio");
                      }}
                      className="shrink-0 absolute top-2 right-2"
                    >
                      <HiX className="text-xs" />
                    </IconButton>
                  </div>
                  <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-100 rounded-full"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[9px] text-muted-foreground">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration || 0)}</span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 h-full px-3 py-3 text-center">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    <FaMusic className="text-muted-foreground/60 text-xs" />
                  </div>
                  <p className="text-xs font-medium text-foreground">Question audio</p>
                  <p className="text-[10px] text-muted-foreground">Drop a file or generate above</p>
                </div>
              )}
            </DroppableZone>
          )}
        </div>
      </div>

      {/* GROUP 2: Answer + Image — single card */}
      <div className="flex-1 min-h-0 rounded-xl border border-border overflow-hidden flex">
        {/* Answer panel */}
        <div className="flex-1 bg-surface p-3 flex flex-col">
          <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1.5 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
            Expected Answer
          </label>
          <textarea
            value={correctAnswer || ""}
            onChange={(e) => {
              if (!slide) return;
              dispatch(
                updateSlide({
                  slideId: slide.id,
                  slideData: { ...grammarSlideData, correctAnswer: e.target.value },
                })
              );
            }}
            className="flex-1 w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm font-medium focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all placeholder:text-muted-foreground/50 resize-none"
            placeholder="Enter expected answer..."
          />
        </div>

        {/* Divider */}
        <div className="w-px bg-border shrink-0" />

        {/* Image panel — fills same space as audio */}
        <div className="flex-1 bg-surface relative overflow-hidden">
          {slide && (
            <DroppableZone
              id={`${slide.id}-image`}
              data={{ id: slide.id, type: "grammar-question-image" }}
              allowedType={["image"]}
              className="absolute inset-0"
              onLocalFileDrop={handleImageFileDrop}
              onClick={() => setActivePanel("Media")}
            >
              <div className="w-full h-full flex items-center justify-center relative">
                {isUploadingImage ? (
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-[10px] text-muted-foreground">Uploading…</p>
                  </div>
                ) : image?.src ? (
                  <>
                    <img src={image.src} alt={image.src} className="w-full h-full object-cover" />
                    <IconButton
                      type="button"
                      disabled={!image?.src}
                      variant="danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveMedia("image");
                      }}
                      className="absolute top-2 right-2"
                    >
                      <HiX className="text-xs" />
                    </IconButton>
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-1.5 text-center">
                    <AiFillPicture className="text-muted-foreground/40 text-2xl" />
                    <p className="text-xs font-medium text-foreground">Question image</p>
                    <p className="text-[10px] text-muted-foreground">
                      Drop a file or generate above
                    </p>
                  </div>
                )}
              </div>
            </DroppableZone>
          )}
        </div>
      </div>

      {slide && isVoiceSettingsOpen && text && (
        <VoiceSettingsModal
          audioData={{
            id: audio?.id ?? null,
            filename: audio?.filename ?? null,
            src: audio?.src ?? null,
          }}
          onClose={() => setIsVoiceSettingsOpen(false)}
          text={text}
          slideId={slide.id}
          voice={DEFAULT_VOICE}
          usageContext={MediaUsageContextSchema.Values.aiTextToSpeech}
          onSave={({ src, id, filename }) => {
            grammarHandlers.addAudio?.(
              { src: src ?? "", id: id ?? "", filename: filename ?? "" },
              slide.id
            );
            setIsVoiceSettingsOpen(false);
          }}
        />
      )}
    </EditorContainer>
  );
}
